import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GoogleReviewService } from '../../api/generated/services/GoogleReviewService';
import type { GoogleReviewAiSettingsView } from '../../types/googleReview';

interface GoogleReviewSettingsState {
  settings: GoogleReviewAiSettingsView | null;
  isSaving: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: GoogleReviewSettingsState = {
  settings: null,
  isSaving: false,
  isLoading: false,
  error: null,
};

export const fetchGoogleReviewSettings = createAsyncThunk(
  'googleReviewSettings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.getAppGoogleReviewSettingsGet();
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Unknown error');
    }
  },
);

export const saveGoogleReviewSettings = createAsyncThunk(
  'googleReviewSettings/saveSettings',
  async (data: GoogleReviewAiSettingsView, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.putAppGoogleReviewSettingsPut(data);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Unknown error');
    }
  },
);

const googleReviewSettingsSlice = createSlice({
  name: 'googleReviewSettings',
  initialState,
  reducers: {
    clearSettingsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGoogleReviewSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGoogleReviewSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchGoogleReviewSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(saveGoogleReviewSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveGoogleReviewSettings.fulfilled, (state, action) => {
        state.isSaving = false;
        state.settings = action.payload;
      })
      .addCase(saveGoogleReviewSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSettingsError } = googleReviewSettingsSlice.actions;

export default googleReviewSettingsSlice.reducer;
